/**
 * How dates and figures are written on screen.
 *
 * One place, because the panel had two date formats at once: most screens printed the
 * API's ISO string straight out (`2026-08-18`) while the people list ran
 * `toLocaleDateString()` and produced `8/13/2026`. Two formats in one product is a small
 * thing that makes a reader do arithmetic — and `8/13` versus `13/8` is genuinely ambiguous
 * to anyone outside the browser's locale.
 *
 * ISO is the house style. It sorts, it never flips day and month, and it matches what the
 * mobile app shows a field user, so a supervisor reading a date over the phone is reading
 * the same characters they are.
 */

/** A calendar date, from either an ISO date or a timestamp. Never the browser's locale. */
export function formatDate(value: string | Date | null | undefined): string {
  if (value === null || value === undefined || value === '') return '—';

  // Already a plain calendar date: leave it exactly as the server sent it. Parsing it into
  // a Date first would apply the browser's timezone and can move it a day.
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) return value;

  const date = typeof value === 'string' ? new Date(value) : value;
  if (Number.isNaN(date.getTime())) return '—';

  const pad = (n: number): string => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

/** A date and the time of day, for a moment rather than a day. */
export function formatDateTime(value: string | Date | null | undefined): string {
  if (value === null || value === undefined || value === '') return '—';

  const date = typeof value === 'string' ? new Date(value) : value;
  if (Number.isNaN(date.getTime())) return '—';

  const pad = (n: number): string => String(n).padStart(2, '0');
  return `${formatDate(date)} ${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

/**
 * An internal key as a person would say it: `field_user` → `Field User`.
 *
 * The role column on the reports screen printed the key itself. Keys are how the code
 * refers to a role; they are not the role's name, and a reader should never be shown one.
 * Where an actual display name is available, use that in preference to this.
 */
export function humanise(key: string | null | undefined): string {
  if (key === null || key === undefined || key === '') return '—';

  return key
    .split(/[_\-.]/)
    .filter((part) => part !== '')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

/**
 * The compliance bands, in one place.
 *
 * The tables and the ranked chart both colour by these. Left duplicated they would drift,
 * and a store shown amber in a chart and red in the table beneath it is worse than either
 * reading alone — the screen would be arguing with itself. Here rather than in a component
 * so neither side of the pair has to import the other.
 */
export function complianceTone(value: number): 'success' | 'warning' | 'danger' {
  return value >= 90 ? 'success' : value >= 60 ? 'warning' : 'danger';
}
